/*
 * Copyright (c) 2024.
 */

/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez Cumbe
 * Date:         20/12/23 - 12:12 PM
 * Module name:  base.service.ts
 * File name:    base.service.ts
 * IDE:          WebStorm
 */


import {Injectable, NgZone} from '@angular/core';
import {debounceTime, filter, fromEvent, Observable, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {ViewportRuler} from '@angular/cdk/scrolling';

export const THROTTLE_TIME = 300;
export const HEIGHT_OFFSET = 300;
export const HEIGHT_ROW = 38;
export const ROW_MIN = 3;

@Injectable()
export class MkadWindowControlService {
  resizeView$: Observable<Event>;
  private lastWindowHeight = 0;
  private rowsLimit = ROW_MIN;
  private resizeSubscribe?: Subscription;

  /**
   * constructor class
   * @param viewportRuler ViewportRuler dependency injection
   * @param ngZone NgZone dependency injector
   */
  constructor(private readonly viewportRuler: ViewportRuler,
              private readonly ngZone: NgZone) {
    this.setRowsLimit(window.innerHeight);
    this.resizeView$ = fromEvent(window, 'resize');
  }

  /**
   * @description
   * Method that response every resize of the windows
   */
  onChangeViewSize() {
    this.resizeSubscribe = this.viewportRuler
      .change(THROTTLE_TIME)
      .subscribe(() => this.ngZone.run(() => {
        const currentHeight = window.innerHeight;
        if (this.lastWindowHeight !== currentHeight) this.setRowsLimit(currentHeight);
      }));
  }

  deleteSubscriber() {
    if (this.resizeSubscribe) this.resizeSubscribe.unsubscribe();
  }

  windowResize(): Observable<number> {
    return this.resizeView$.pipe(
      map(() => {
        const currentHeight = window.innerHeight;
        const heightChanged = currentHeight !== this.lastWindowHeight;
        this.lastWindowHeight = currentHeight;
        return {heightChanged, currentHeight};
      }),
      filter(({heightChanged}) => heightChanged),
      debounceTime(THROTTLE_TIME),
      map(({currentHeight}) => {
        const availableHeight = currentHeight - HEIGHT_OFFSET;
        return Math.floor(availableHeight / HEIGHT_ROW);
      })
    );
  }

  /**
   * Method that calculate the new limit for the rows
   * @param currentHeight
   */
  setRowsLimit(currentHeight: number) {
    this.lastWindowHeight = currentHeight;
    const availableHeight = currentHeight - HEIGHT_OFFSET;
    this.rowsLimit = Math.floor(availableHeight / HEIGHT_ROW);
    this.rowsLimit = this.rowsLimit < ROW_MIN ? ROW_MIN : this.rowsLimit
  }

  /**
   * @description
   * Method that return the rows limit
   */
  get rowLimit(): number {
    return this.rowsLimit;
  }

}
